import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { getFirst } from '../outline/utils.js'
import { getSheetRevisions, deleteSheetRevision, deleteDrawingSheet } from '../services/sheets.js'
import { getRevisionVersions, scrubRevisionQc } from '../services/qcVersions.js'
import { getSheetUsageInModels } from '../services/bimModeler.js'
import { ctx } from '../outline/ctx.js'
import { wf } from '../outline/state.js'

/**
 * Confirmation modal for removing a Drawing Sheet's version from a milestone.
 *
 * This is a per-milestone removal, NOT a project delete. It deletes only the one
 * Sheet Revision that links this sheet to the current milestone — the Drawing Sheet
 * record and any other milestone versions are untouched. The single exception is the
 * sole-version case below, which is the only path that touches the project.
 *
 * Logic:
 *   1. Fetches all revisions for the sheet on mount, plus this revision's QC round chain
 *      (QC is revision-scoped — its rounds/findings/comments/PDFs belong to this milestone
 *      instance and are scrubbed with it; see scrubRevisionQc).
 *   2. Recency is decided by POSTED STATE, not by fragile date math: a posted
 *      revision is an earlier/locked version; an unposted revision is the current
 *      (newest) one being worked on. Removal is blocked only if a genuinely newer
 *      version exists (see `strictlyNewer`).
 *   3. If this is the ONLY revision AND it is an Original Release, the sheet exists
 *      nowhere else → removing it also deletes the Drawing Sheet record.
 *   4. Otherwise → deletes only this Sheet Revision. Re-adding the sheet later is
 *      possible, but any scrubbed QC history does NOT come back.
 *   Typing "DELETE" is required for either irreversible outcome: the whole-sheet
 *   delete, OR any removal that scrubs existing QC work.
 *
 * @param {{ sheet, milestoneId, onClose, onRemoved }} props
 *   sheet       — normalized sheet object { id, revisionId, revision, title }
 *   milestoneId — current milestone (used for cache refresh by caller)
 *   onClose     — called on Cancel / backdrop click
 *   onRemoved({ sheetAlsoDeleted: boolean }) — called after successful removal
 */
export default function RemoveSheetModal({ sheet, onClose, onRemoved }) {
  const [loading,    setLoading]    = useState(true)
  const [newerRevs,  setNewerRevs]  = useState(null)   // array of blocking revisions, or null if none
  const [deleteSheet, setDeleteSheet] = useState(false) // true = will also delete Drawing Sheet
  const [qcRounds,   setQcRounds]   = useState(0)      // QC rounds on THIS revision — scrubbed with it
  const [confirmText, setConfirmText] = useState('')
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState(null)

  const isOriginalRelease = sheet.revision?.fields?.['Type'] === 'Original Release'

  // SHEET LINK SYNC (2026-10-20, Matt): before a whole-sheet delete, read every AlderCAD model in
  // the project for a sheet linked to this one. null = checking, [] = none, 'unknown' = the check
  // failed (treated as cautiously as real work — never as "nothing there").
  const [cadUses, setCadUses] = useState(null)
  const projectId = ctx.recordId || wf.zoomProjectId || null
  useEffect(() => {
    if (!deleteSheet) return
    if (!projectId) { setCadUses('unknown'); return }
    let cancelled = false
    setCadUses(null)
    getSheetUsageInModels(projectId, sheet.id)
      .then(u => { if (!cancelled) setCadUses(u) })
      .catch(() => { if (!cancelled) setCadUses('unknown') })
    return () => { cancelled = true }
  }, [deleteSheet, sheet.id, projectId])
  const cadWorked = Array.isArray(cadUses) && cadUses.some(u => u.unreadable || u.viewports > 0 || u.schedules > 0 || u.pdfInsert)
  const cadCautious = deleteSheet && (cadWorked || cadUses === 'unknown')
  const sheetNumber = String(sheet.sheetNumber || (sheet.title || '').split(/\s+[-—]\s+/)[0] || '').trim()
  // a sheet with AlderCAD work asks for its own NUMBER, not the usual DELETE — a deliberate act
  const confirmWord = cadCautious && sheetNumber ? sheetNumber : 'DELETE'

  // QC round chain for this revision — its rounds/comments/markup are deleted along with the
  // revision (QC is revision-scoped), so the warning must say so concretely.
  useEffect(() => {
    if (!sheet.revisionId) return
    let cancelled = false
    getRevisionVersions(sheet.revisionId)
      .then(v => { if (!cancelled) setQcRounds((v || []).length) })
      .catch(() => {})
    return () => { cancelled = true }
  }, [sheet.revisionId])

  useEffect(() => {
    if (!sheet.id || !sheet.revisionId) { setLoading(false); return }
    getSheetRevisions(sheet.id)
      .then(revisions => {
        // Recency by POSTED state, not by date math (dates are fragile — an in-progress milestone has
        // no posted date). The logical train:
        //   • a POSTED revision is an earlier, locked version;
        //   • an UNPOSTED revision is the current version being worked on, and is the NEWEST.
        // So a revision is blocked from removal only if something genuinely NEWER exists:
        //   • removing an unposted (current) revision → never blocked (posted ones are earlier;
        //     another unposted sibling isn't "newer");
        //   • removing a posted revision → blocked if an unposted (in-progress) revision exists,
        //     or a posted revision with a later posted date exists.
        const postedOf = r => getFirst(r.fields['Posted Date (from Deliverables)']) || ''
        const curPosted = postedOf(sheet.revision || {})
        const strictlyNewer = r => {
          if (r.id === sheet.revisionId) return false
          if (!curPosted) return false                 // current is the in-progress (newest) version
          const rp = postedOf(r)
          if (!rp) return true                         // r is in-progress → newer than a posted current
          return rp > curPosted                        // both posted → later posted date is newer
        }
        const newer = revisions.filter(strictlyNewer)
          .sort((a, b) => {
            const pa = postedOf(a), pb = postedOf(b)
            if (!pa && pb) return -1                    // unposted (newest) first in the list
            if (pa && !pb) return 1
            return String(pb).localeCompare(String(pa))
          })

        if (newer.length > 0) {
          setNewerRevs(newer)
        } else {
          setNewerRevs([])
          // Sole revision + Original Release ⇒ removing it deletes the whole sheet (it lives nowhere else).
          setDeleteSheet(revisions.length === 1 && isOriginalRelease)
        }
      })
      .catch(err => {
        console.error('[RemoveSheetModal] fetch revisions error', err)
        setError('Could not load revision history. Please try again.')
        setNewerRevs([])
      })
      .finally(() => setLoading(false))
  }, [sheet.id, sheet.revisionId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Typing "DELETE" is required for any IRREVERSIBLE outcome: the whole-sheet delete, or a
  // removal that scrubs existing QC work (rounds/comments/markup don't come back on re-add).
  const requireType = deleteSheet || qcRounds > 0
  const checkingCad = deleteSheet && cadUses === null

  async function handleConfirm() {
    if (checkingCad) return
    if (requireType && confirmText !== confirmWord) return
    setSaving(true)
    setError(null)
    try {
      // Scrub the revision's QC chain FIRST (rounds, findings, comments, runs, stored PDFs) —
      // QC is revision-scoped, so it goes with the revision. Abort on failure rather than
      // leaving orphaned QC rows behind a deleted revision.
      if (qcRounds > 0) await scrubRevisionQc(sheet.revisionId, deleteSheet ? sheet.id : null)
      await deleteSheetRevision(sheet.revisionId)
      if (deleteSheet) {
        await deleteDrawingSheet(sheet.id)
      }
      onRemoved({ sheetAlsoDeleted: deleteSheet })
    } catch (e) {
      console.error('[RemoveSheetModal] delete error', e)
      setError('Deletion failed. Please try again.')
      setSaving(false)
    }
  }

  const blocked = Array.isArray(newerRevs) && newerRevs.length > 0

  return createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 600,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10,
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        width: 420, maxWidth: 'calc(100vw - 32px)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px 10px',
          borderBottom: '0.5px solid var(--aj-border)',
        }}>
          <span style={{
            fontFamily: '"DM Sans",sans-serif', fontWeight: 600,
            fontSize: 13, color: 'var(--aj-text)',
          }}>
            Remove from Milestone
          </span>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'none', border: 'none', cursor: saving ? 'default' : 'pointer',
              fontSize: 18, color: 'var(--aj-text-mid)', lineHeight: 1, padding: 0,
            }}
          >×</button>
        </div>

        {/* Sheet label */}
        <div style={{
          padding: '8px 16px 6px',
          fontFamily: '"DM Mono",monospace', fontSize: 11,
          color: 'var(--aj-text-mid)',
          borderBottom: '0.5px solid var(--aj-border)',
        }}>
          {sheet.title || sheet.id}
        </div>

        {/* Body */}
        <div style={{ padding: '14px 16px' }}>
          {loading && (
            <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', margin: 0 }}>
              Checking revision history…
            </p>
          )}

          {/* Blocked state — newer revisions exist */}
          {!loading && blocked && (
            <>
              <p style={{
                fontFamily: '"DM Sans",sans-serif', fontSize: 12,
                color: 'var(--aj-text)', margin: '0 0 10px',
              }}>
                This version can&rsquo;t be removed because a newer version of this sheet exists.
                Remove the newer version{newerRevs.length > 1 ? 's' : ''} first:
              </p>
              <ul style={{ margin: '0 0 4px', padding: '0 0 0 18px' }}>
                {newerRevs.map(r => {
                  const type  = r.fields['Type'] || 'Revision'
                  const ms    = getFirst(r.fields['Full Deliverable Name (from Deliverables)'])
                    || getFirst(r.fields['Name (from Deliverable) (from Deliverables)'])
                    || r.id
                  return (
                    <li key={r.id} style={{
                      fontFamily: '"DM Sans",sans-serif', fontSize: 12,
                      color: 'var(--aj-text)', marginBottom: 4,
                    }}>
                      <strong>{type}</strong>{ms ? ' — ' + ms : ''}
                    </li>
                  )
                })}
              </ul>
            </>
          )}

          {/* Removable state */}
          {!loading && !blocked && !error && (
            <>
              {deleteSheet ? (
                <p style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5,
                  color: 'var(--aj-text)', margin: '0 0 12px',
                }}>
                  This is the sheet&rsquo;s only version, and it appears on this milestone alone.
                  Removing it here will <strong>delete the Drawing Sheet from the project entirely</strong> —
                  it exists nowhere else. This can&rsquo;t be undone.
                </p>
              ) : (
                <p style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5,
                  color: 'var(--aj-text)', margin: '0 0 12px',
                }}>
                  This removes the sheet&rsquo;s version on this milestone only.
                  The Drawing Sheet stays in the project and on any other milestone where it appears.
                  You can add it back to this milestone at any time{qcRounds > 0 ? ', but its QC history will not come back' : ''}.
                </p>
              )}
              {deleteSheet && checkingCad && (
                <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', margin: '0 0 12px' }}>
                  Checking AlderCAD for drawings on this sheet…
                </p>
              )}
              {deleteSheet && cadWorked && (
                <div style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5,
                  color: '#b91c1c', margin: '0 0 12px', padding: '9px 11px', borderRadius: 6,
                  border: '0.5px solid #dc2626', background: 'rgba(220,38,38,0.06)',
                }}>
                  <strong>This sheet has work in AlderCAD.</strong>
                  <ul style={{ margin: '6px 0 6px', padding: '0 0 0 18px' }}>
                    {cadUses.map((u, i) => (
                      <li key={i}>
                        {u.unreadable
                          ? <>The model for {u.conceptName} could not be read, so it may have drawings on this sheet.</>
                          : <>{u.conceptName}: {u.sheetNumber} {u.sheetName} — {[
                              u.viewports ? `${u.viewports} drawing${u.viewports !== 1 ? 's' : ''}` : '',
                              u.schedules ? `${u.schedules} schedule${u.schedules !== 1 ? 's' : ''}` : '',
                              u.pdfInsert ? 'an inserted PDF' : '',
                            ].filter(Boolean).join(', ')} on it.</>}
                      </li>
                    ))}
                  </ul>
                  Deleting it here leaves that AlderCAD sheet unlinked. Its drawings are kept, but it will not
                  issue until someone links it to another sheet. If the sheet is being replaced, add the new sheet
                  first and link it in AlderCAD.
                </div>
              )}
              {deleteSheet && cadUses === 'unknown' && (
                <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, color: '#b91c1c', margin: '0 0 12px' }}>
                  AlderCAD could not be checked for drawings on this sheet. Treat it as if it has work there.
                </p>
              )}
              {deleteSheet && Array.isArray(cadUses) && cadUses.length > 0 && !cadWorked && (
                <p style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'var(--aj-text-mid)', margin: '0 0 12px' }}>
                  This sheet is set up in AlderCAD but has nothing drawn on it yet. It will show there as unlinked.
                </p>
              )}
              {qcRounds > 0 && (
                <p style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5,
                  color: '#b8540a', margin: '0 0 12px',
                }}>
                  This milestone&rsquo;s QC work for the sheet — <strong>{qcRounds} round{qcRounds !== 1 ? 's' : ''}</strong> of
                  review, including all comments, statuses, and markup — will be <strong>permanently deleted</strong> with it.
                </p>
              )}

              {requireType && <>
                <label style={{
                  display: 'block',
                  fontFamily: '"DM Sans",sans-serif', fontSize: 11,
                  color: 'var(--aj-text-mid)', marginBottom: 6,
                }}>
                  Type <strong style={{ fontFamily: '"DM Mono",monospace', letterSpacing: '0.05em' }}>{confirmWord}</strong> to confirm
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  disabled={saving}
                  autoFocus
                  placeholder={confirmWord}
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    fontFamily: '"DM Mono",monospace', fontSize: 13,
                    padding: '6px 10px', borderRadius: 4,
                    border: confirmText === confirmWord
                      ? '0.5px solid #dc2626'
                      : '0.5px solid var(--aj-border-mid)',
                    outline: 'none', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)',
                  }}
                />
              </>}
            </>
          )}

          {error && (
            <p style={{
              fontFamily: '"DM Sans",sans-serif', fontSize: 11,
              color: '#dc2626', margin: '10px 0 0',
            }}>
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '10px 16px 14px',
          borderTop: '0.5px solid var(--aj-border)',
        }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              fontFamily: '"DM Sans",sans-serif', fontSize: 12,
              padding: '5px 14px', borderRadius: 4,
              border: '0.5px solid var(--aj-border-mid)',
              background: 'transparent', color: 'var(--aj-text-mid)',
              cursor: saving ? 'default' : 'pointer',
            }}
          >
            Cancel
          </button>
          {!loading && !blocked && (() => {
            const disabled = (requireType && confirmText !== confirmWord) || saving || checkingCad
            return (
              <button
                onClick={handleConfirm}
                disabled={disabled}
                style={{
                  fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600,
                  padding: '5px 14px', borderRadius: 4,
                  border: 'none',
                  // Red for any irreversible outcome — whole-sheet delete OR a QC-history scrub.
                  background: requireType ? '#dc2626' : '#07272D', color: '#fff',
                  cursor: disabled ? 'default' : 'pointer',
                  opacity: disabled ? 0.45 : 1,
                }}
              >
                {saving
                  ? (deleteSheet ? 'Deleting…' : 'Removing…')
                  : deleteSheet ? 'Delete Sheet' : 'Remove from Milestone'}
              </button>
            )
          })()}
        </div>
      </div>
    </div>,
    document.body
  )
}
